import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  EvoPermissionCheckCallId,
  EvoPermissionCheckCredentials,
  EvoPermissionCheckSnapshot,
  EvoPermissionCheckStepId,
  EvoPermissionCheckStepStatus,
  evoPermissionCheckService,
} from '../../services/firebaseFunctions';
import { StatusChip } from './EvoStepSection';
import {
  countSteps,
  failuresLabel,
  getLeftoverNotice,
  testedAccountSentence,
  PERMISSION_CHECK_GROUP_LABELS,
  PERMISSION_CHECK_GROUPS,
  PERMISSION_CHECK_STEPS,
  PERMISSION_CHECK_TOTAL_STEPS,
  stepChip,
  StepChip,
  stepDetailText,
} from './evoPermissionCheckStatus';

/** Tentativas consecutivas de uma mesma etapa em falha de rede antes de desistir (replay seguro no servidor). */
const MAX_CONSECUTIVE_NETWORK_ATTEMPTS = 3;
const NETWORK_RETRY_DELAY_MS = 2000;
const TRANSIENT_CODES = ['unavailable', 'deadline-exceeded', 'unknown', 'internal', 'cancelled'];

const NETWORK_START_MESSAGE = 'Não foi possível falar com o servidor. Confira a conexão e tente de novo. Nada foi gravado.';
const NETWORK_LOST_MESSAGE = (done: number) =>
  `A conexão caiu depois de ${done} de ${PERMISSION_CHECK_TOTAL_STEPS} verificações. Nada foi gravado. Se o teste já tinha criado o webhook de teste, o Reedy o apaga no servidor; se não conseguir confirmar, o aviso com o ID aparece na etapa 1.`;

const isTransientError = (error: unknown) => {
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code !== 'string') return true; // falha de rede sem código
  return TRANSIENT_CODES.includes(code.replace(/^functions\//, ''));
};

const errorText = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

const conflictIdOf = (error: unknown): string | undefined => {
  const details = (error as { details?: { conflictingWebhookConfigId?: unknown } } | null)?.details;
  return typeof details?.conflictingWebhookConfigId === 'string' ? details.conflictingWebhookConfigId : undefined;
};

// ── Copiar ───────────────────────────────────────────────────────────────────

/** Botão que copia exatamente o texto recebido (ID, marcador ou detalhe da etapa). */
export const CopyButton: React.FC<{ text: string; label: string; ariaLabel?: string }> = ({ text, label, ariaLabel }) => {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') return undefined;
    const timer = setTimeout(() => setState('idle'), 2500);
    return () => clearTimeout(timer);
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={copy} aria-label={ariaLabel}>
        {state === 'copied' ? 'Copiado' : label}
      </Button>
      {state === 'failed' && (
        <Typography variant="caption" color="error" role="status">
          Não foi possível copiar. Copie manualmente.
        </Typography>
      )}
    </>
  );
};

// ── Lista por rota (usada aqui e no "Ver resultado" da etapa 1) ───────────────

export interface PermissionCheckRow {
  id: EvoPermissionCheckStepId;
  status: EvoPermissionCheckStepStatus;
  httpStatus: number | null;
  message: string | null;
  title?: string;
  /** Substitui o chip padrão (por exemplo, "Testando…"). */
  chip?: StepChip;
  /** Linha de apoio sob a mensagem (por exemplo, a contagem regressiva do limite de requisições). */
  note?: string;
}

interface StepListProps {
  rows: PermissionCheckRow[];
  simulated: boolean;
  showRoutes: boolean;
}

/** Lista por rota agrupada em Webhooks / Vendas / Membros. Textos sempre como texto, nunca como HTML. */
export const PermissionCheckStepList: React.FC<StepListProps> = ({ rows, simulated, showRoutes }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {PERMISSION_CHECK_GROUPS.map((group) => {
      const metas = PERMISSION_CHECK_STEPS.filter((meta) => meta.group === group);
      const groupRows = metas.map((meta) => ({ meta, row: rows.find((row) => row.id === meta.id) })).filter((entry) => entry.row);
      if (groupRows.length === 0) return null;
      return (
        <Box key={group} component="section" aria-label={PERMISSION_CHECK_GROUP_LABELS[group]}>
          <Typography variant="overline" color="text.secondary" component="h3" sx={{ display: 'block', lineHeight: 2 }}>
            {PERMISSION_CHECK_GROUP_LABELS[group]}
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column' }}>
            {groupRows.map(({ meta, row }) => {
              const current = row as PermissionCheckRow;
              const chip = current.chip ?? stepChip(current, simulated);
              const hasResult = current.status !== 'pending';
              return (
                <Box
                  component="li"
                  key={meta.id}
                  sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, py: 1, borderTop: 1, borderColor: 'divider' }}
                >
                  <Box sx={{ flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {current.title || meta.title}
                    </Typography>
                    {showRoutes && hasResult && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                        {meta.route}
                      </Typography>
                    )}
                    {current.message && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {current.message}
                      </Typography>
                    )}
                    {current.note && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} role="status">
                        {current.note}
                      </Typography>
                    )}
                    {current.status === 'failed' && current.message && (
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <CopyButton
                          text={stepDetailText(current)}
                          label="Copiar detalhe"
                          ariaLabel={`Copiar detalhe: ${current.title || meta.title}`}
                        />
                      </Box>
                    )}
                  </Box>
                  <StatusChip {...chip} />
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    })}
  </Box>
);

// ── Diálogo bloqueante ───────────────────────────────────────────────────────

type Wait = { reason: 'rate_limited' | 'in_progress'; secondsLeft: number };

type View =
  | { kind: 'running'; snapshot: EvoPermissionCheckSnapshot | null; wait: Wait | null }
  | { kind: 'saving'; snapshot: EvoPermissionCheckSnapshot }
  | { kind: 'done'; snapshot: EvoPermissionCheckSnapshot; saveError: string | null }
  | { kind: 'error'; snapshot: EvoPermissionCheckSnapshot | null; message: string; network: boolean };

export interface PermissionCheckDoneInfo {
  /** `close`: fechar (teste de um cadastro já salvo). `back`: voltar ao formulário de credenciais. */
  action: 'close' | 'back';
  /** O servidor pode ter gravado um aviso de webhook de teste não apagado: a tela deve recarregar o cadastro. */
  maybeLeftover: boolean;
}

export interface PermissionCheckSaveResult {
  success: boolean;
  error?: string;
}

interface Props {
  open: boolean;
  configId: string;
  /** `saved`: credenciais já salvas (J1). `credentials`: valores do formulário, antes de gravar (J2). */
  mode: 'saved' | 'credentials';
  /** Só em `credentials`. Vive no estado de quem chama; aqui só é reenviado a cada etapa. */
  credentials?: EvoPermissionCheckCredentials;
  /** J2: grava as credenciais com a prova do teste; chamado uma vez, só com o teste sem falhas. */
  onSaveCredentials?: (runId: string, snapshot: EvoPermissionCheckSnapshot) => Promise<PermissionCheckSaveResult>;
  /** J2: o teste nem começou (conflito de DNS+token, validação); o formulário mostra o erro. */
  onStartError?: (error: { message: string; conflictingWebhookConfigId?: string }) => void;
  onDone: (info: PermissionCheckDoneInfo) => void;
}

const CALL_STEP_IDS = (call: EvoPermissionCheckCallId | null) => PERMISSION_CHECK_STEPS.filter((meta) => meta.call === call).map((meta) => meta.id);

const EvoPermissionCheckDialog: React.FC<Props> = ({ open, configId, mode, credentials, onSaveCredentials, onStartError, onDone }) => {
  const [view, setView] = useState<View>({ kind: 'running', snapshot: null, wait: null });
  const [attempt, setAttempt] = useState(0);
  const latest = useRef({ configId, mode, credentials, onSaveCredentials, onStartError });
  latest.current = { configId, mode, credentials, onSaveCredentials, onStartError };
  const titleId = 'evo-permission-check-title';

  useEffect(() => {
    if (!open) return undefined;
    const ctl = { cancelled: false };
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const intervals = new Set<ReturnType<typeof setInterval>>();
    setView({ kind: 'running', snapshot: null, wait: null });

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
          timers.delete(timer);
          resolve();
        }, ms);
        timers.add(timer);
      });

    // Um único intervalo por vez (o laço é sequencial) e relógio de parede: não acumula atraso em aba em segundo plano.
    const countdown = (seconds: number, onTick: (left: number) => void) =>
      new Promise<void>((resolve) => {
        const total = Math.max(1, Math.ceil(Number.isFinite(seconds) ? seconds : 1));
        const deadline = Date.now() + total * 1000;
        onTick(total);
        const timer = setInterval(() => {
          const left = Math.ceil((deadline - Date.now()) / 1000);
          if (left <= 0) {
            clearInterval(timer);
            intervals.delete(timer);
            resolve();
          } else {
            onTick(left);
          }
        }, 1000);
        intervals.add(timer);
      });

    const drive = async () => {
      const { configId: id, mode: runMode, credentials: values } = latest.current;
      let snapshot: EvoPermissionCheckSnapshot;
      try {
        snapshot =
          runMode === 'credentials' && values
            ? await evoPermissionCheckService.start({ configId: id, mode: 'credentials', credentials: values })
            : await evoPermissionCheckService.start({ configId: id, mode: 'saved' });
      } catch (error) {
        if (ctl.cancelled) return;
        const network = isTransientError(error);
        const message = network ? NETWORK_START_MESSAGE : errorText(error, 'Não foi possível iniciar o teste de permissões.');
        if (runMode === 'credentials' && latest.current.onStartError) {
          latest.current.onStartError({ message, conflictingWebhookConfigId: conflictIdOf(error) });
        } else {
          setView({ kind: 'error', snapshot: null, message, network });
        }
        return;
      }

      for (;;) {
        if (ctl.cancelled) return;
        setView({ kind: 'running', snapshot, wait: null });
        if (snapshot.finished) break;
        if (snapshot.waiting) {
          const { reason, retryAfterSeconds } = snapshot.waiting;
          const current = snapshot;
          await countdown(retryAfterSeconds, (left) => {
            if (!ctl.cancelled) setView({ kind: 'running', snapshot: current, wait: { reason, secondsLeft: left } });
          });
          if (ctl.cancelled) return;
        }
        const step = snapshot.nextStep;
        if (!step) {
          setView({ kind: 'error', snapshot, message: 'O teste de permissões terminou de forma inesperada. Teste de novo.', network: false });
          return;
        }

        let next: EvoPermissionCheckSnapshot | null = null;
        for (let tries = 1; next === null; tries += 1) {
          try {
            const run = latest.current;
            next = await evoPermissionCheckService.runStep(
              run.mode === 'credentials' && run.credentials
                ? { runId: snapshot.runId, step, credentials: run.credentials }
                : { runId: snapshot.runId, step },
            );
          } catch (error) {
            if (ctl.cancelled) return;
            if (isTransientError(error) && tries < MAX_CONSECUTIVE_NETWORK_ATTEMPTS) {
              await sleep(NETWORK_RETRY_DELAY_MS);
              if (ctl.cancelled) return;
            } else {
              const network = isTransientError(error);
              setView({
                kind: 'error',
                snapshot,
                message: network ? NETWORK_LOST_MESSAGE(snapshot.completedCount) : errorText(error, 'Não foi possível concluir o teste de permissões. Teste de novo.'),
                network,
              });
              return;
            }
          }
        }
        snapshot = next;
      }

      const save = latest.current.onSaveCredentials;
      if (snapshot.outcome === 'passed' && latest.current.mode === 'credentials' && save) {
        setView({ kind: 'saving', snapshot });
        let result: PermissionCheckSaveResult;
        try {
          result = await save(snapshot.runId, snapshot);
        } catch (error) {
          result = { success: false, error: errorText(error, 'Erro ao salvar credenciais') };
        }
        if (ctl.cancelled) return;
        setView({ kind: 'done', snapshot, saveError: result.success ? null : result.error || 'Erro ao salvar credenciais' });
        return;
      }
      setView({ kind: 'done', snapshot, saveError: null });
    };

    drive().catch(() => {
      // Falha inesperada do próprio laço: nunca deixa o diálogo bloqueado sem saída.
      if (!ctl.cancelled) {
        setView({ kind: 'error', snapshot: null, message: 'Não foi possível concluir o teste de permissões. Teste de novo.', network: false });
      }
    });
    return () => {
      ctl.cancelled = true;
      timers.forEach(clearTimeout);
      intervals.forEach(clearInterval);
    };
  }, [open, attempt]);

  const snapshot = view.snapshot;
  const settled = view.kind === 'done' || view.kind === 'error';
  const leftoverNotice = snapshot?.leftover ? getLeftoverNotice({ ...snapshot.leftover, at: '' }) : null;
  const simulated = snapshot?.simulated === true;
  const isCredentials = mode === 'credentials';

  const finishAction = isCredentials ? 'back' : 'close';
  const handleFinish = () =>
    onDone({ action: finishAction, maybeLeftover: Boolean(snapshot?.leftover) || view.kind === 'error' });
  const handleRetry = () => setAttempt((value) => value + 1);

  // ── Linhas ──
  const wait = view.kind === 'running' ? view.wait : null;
  const activeIds = view.kind === 'running' && snapshot ? CALL_STEP_IDS(snapshot.nextStep) : [];
  const rows: PermissionCheckRow[] = PERMISSION_CHECK_STEPS.map((meta) => {
    const step = snapshot?.steps.find((item) => item.id === meta.id);
    const status: EvoPermissionCheckStepStatus = step?.status ?? 'pending';
    const base: PermissionCheckRow = {
      id: meta.id,
      status,
      httpStatus: step?.httpStatus ?? null,
      message: step?.message ?? null,
      title: step?.title,
    };
    if (status === 'pending' && activeIds.includes(meta.id)) {
      if (wait?.reason === 'rate_limited') {
        return {
          ...base,
          chip: { label: 'Aguardando limite', tone: 'warning' },
          note: meta.id === activeIds[0] ? `Aguardando limite… continua sozinho em ${wait.secondsLeft} s` : undefined,
        };
      }
      return { ...base, chip: { label: wait?.reason === 'in_progress' ? 'Verificando…' : 'Testando…', tone: 'info' } };
    }
    return base;
  });
  // Teste interrompido: só o que tem resultado; o resto não é afirmado (o servidor pode ter concluído sem o navegador).
  const visibleRows = view.kind === 'error' ? rows.filter((row) => row.status !== 'pending') : rows;

  // ── Cabeçalho por estado ──
  const counts = snapshot ? countSteps(snapshot.steps) : null;
  let title = isCredentials ? 'Verificando permissões do token' : 'Testando permissões do token';
  let intro: string | null = isCredentials
    ? 'As credenciais só são salvas se todas as verificações passarem. O Reedy vai criar e apagar um webhook de teste na conta.'
    : 'Não feche esta tela. O Reedy está chamando a EVO e vai criar e apagar um webhook de teste na conta.';
  let headChip: StepChip | null = null;
  let tail: string | null = 'Aguarde: o teste leva alguns segundos e a tela só libera ao terminar.';

  if (view.kind === 'saving') {
    tail = 'Teste aprovado. Salvando as credenciais…';
  } else if (view.kind === 'done' && counts) {
    const failed = counts.failed > 0 || view.snapshot.outcome === 'failed';
    const savedFailed = view.saveError !== null;
    if (failed || savedFailed) {
      title = isCredentials ? 'Credenciais não salvas' : 'Faltam permissões';
      headChip = failed ? { label: failuresLabel(counts.failed), tone: 'error' } : null;
      intro = savedFailed
        ? null
        : isCredentials
          ? 'O token não tem todas as permissões que o Reedy usa, então as credenciais não foram salvas. Libere as permissões na EVO e teste de novo, ou volte para conferir o DNS e o token.'
          : counts.failed === 1
            ? `1 de ${PERMISSION_CHECK_TOTAL_STEPS} verificações falhou. Libere a permissão no painel da EVO e teste de novo.`
            : `${counts.failed} de ${PERMISSION_CHECK_TOTAL_STEPS} verificações falharam. Libere as permissões no painel da EVO e teste de novo.`;
    } else {
      title = simulated ? 'Teste simulado' : 'Permissões verificadas';
      headChip = {
        label: counts.noSample > 0 ? `${counts.ok} OK · ${counts.noSample} sem venda` : `${counts.ok} de ${PERMISSION_CHECK_TOTAL_STEPS} OK`,
        tone: 'ok',
      };
      intro =
        counts.noSample > 0
          ? `${counts.noSample === 1 ? '1 verificação não pôde rodar' : `${counts.noSample} verificações não puderam rodar`} por falta de venda para testar. Isso não impede salvar as credenciais. Teste de novo quando houver uma venda.`
          : 'O token tem acesso a tudo o que o Reedy usa na EVO. O webhook de teste foi criado e apagado.';
    }
    tail = isCredentials ? null : 'Este resultado ficou salvo no cadastro.';
  } else if (view.kind === 'error') {
    title = 'O teste foi interrompido';
    headChip = view.network ? { label: 'Conexão perdida', tone: 'warning' } : null;
    intro = view.message;
    tail = null;
  }

  const dismissable = settled && !leftoverNotice;
  const progressValue = ((snapshot?.completedCount ?? 0) / PERMISSION_CHECK_TOTAL_STEPS) * 100;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
      disableEscapeKeyDown={!dismissable}
      onClose={(_event, reason) => {
        // Bloqueante: Esc e clique fora só valem com o teste encerrado e sem aviso de webhook de teste pendente.
        if (dismissable && (reason === 'escapeKeyDown' || reason === 'backdropClick')) handleFinish();
      }}
      sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' } } }}
    >
      <DialogTitle component="div" id={`${titleId}-head`} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box component="h2" id={titleId} sx={{ m: 0, font: 'inherit', flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>
          {title}
        </Box>
        {headChip && <StatusChip {...headChip} />}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          {intro && <Typography variant="body2">{intro}</Typography>}

          {simulated && (
            <Alert severity="info">
              Este ambiente está em modo simulado: nenhuma chamada real foi feita à EVO e o token não foi testado de verdade.
            </Alert>
          )}

          {view.kind === 'done' && view.saveError && <Alert severity="error">{view.saveError}</Alert>}

          {leftoverNotice && (
            <Alert severity="warning" role="alert">
              <AlertTitle>Atenção: webhook de teste ficou na EVO</AlertTitle>
              {leftoverNotice.evoIds.length > 0
                ? `O webhook de teste (ID ${leftoverNotice.evoIds.join(', ')}) não foi apagado. Apague-o no painel da EVO para ele não ficar na conta.`
                : `O webhook de teste pode não ter sido apagado. Na EVO, procure um webhook cuja URL contenha ${leftoverNotice.marker} e apague-o.`}
              {isCredentials &&
                ` ${
                  testedAccountSentence(snapshot?.leftover?.dns ?? credentials?.dns) ??
                  'Ele pode estar na conta testada, que nem sempre é a conta salva neste cadastro.'
                }`}
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {leftoverNotice.copyActions.map((action) => (
                  <CopyButton key={action.label} text={action.text} label={action.label} />
                ))}
              </Box>
            </Alert>
          )}

          {view.kind !== 'error' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" aria-live="polite">
                  {`${snapshot?.completedCount ?? 0} de ${PERMISSION_CHECK_TOTAL_STEPS}`}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                aria-label="Progresso do teste de permissões"
                sx={{ borderRadius: 1, height: 6 }}
              />
            </Box>
          )}

          {visibleRows.length > 0 && <PermissionCheckStepList rows={visibleRows} simulated={simulated} showRoutes={settled} />}

          {tail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              {!settled && <CircularProgress size={16} color="inherit" />}
              <Typography variant="caption">{tail}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      {settled && (
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, '& > :not(style)': { ml: 0 } }}>
          {view.kind === 'done' && view.snapshot.outcome === 'passed' && !isCredentials && !view.saveError ? (
            <Button variant="contained" onClick={handleFinish}>
              {leftoverNotice ? 'Entendi, fechar' : 'Fechar'}
            </Button>
          ) : (
            <>
              <Button onClick={handleFinish}>
                {isCredentials ? 'Voltar e editar' : leftoverNotice ? 'Entendi, fechar' : 'Fechar'}
              </Button>
              <Button variant="contained" onClick={handleRetry}>
                Testar de novo
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default EvoPermissionCheckDialog;
